import { useEffect, useRef, useState } from 'react';
import { apiService, CHECKOUT_API_CONFIG } from '../../services/apiService';

/**
 * Collect Group inline card step, rendered INSIDE the checkout card — including the 3DS challenge.
 *
 * Card entry happens in Rapyd's Checkout Toolkit frame, mounted here. The hard part is 3DS: the
 * toolkit refuses to render the bank challenge nested (it parks on a "3DS pending" view and never
 * hands the challenge out), and the acquirer's own pages send `frame-ancestors 'self'`, so the
 * obvious fallbacks all end with the shopper looking at an acquirer-branded page we don't want them
 * to see.
 *
 * The way around both: Rapyd's hosted page resolves the challenge by reading `payment.redirect_url`
 * off its own client checkout endpoint and navigating there. That endpoint
 * (api.rapyd.net/v1/checkout/client/{id}) is CORS-open (`access-control-allow-origin: *`) and needs
 * only the checkout id — no API key — and the URL it returns
 * (checkout.rapyd.net/3ds-v2/challenge.html) sends NO framing headers at all. So we fetch that URL
 * ourselves and host the bank challenge in our own iframe.
 *
 * When the challenge finishes, Rapyd sends that iframe on to the acquirer's return URL, which its
 * own CSP then refuses to render — which is exactly what we want: the acquirer's page cannot paint
 * inside our frame, and we swap the (now-dead) iframe for our own result the moment polling
 * confirms the outcome. The shopper never sees an acquirer page.
 *
 * {@link leave} remains only as a fallback for when the challenge URL can't be obtained.
 */

type SessionResponse = {
  statusUrl?: string;
  /** Backend-built masked Collect Group checkout URL for the fallback button. */
  directUrl?: string;
  hostedPageUrl?: string;
  checkoutId?: string;
  toolkitScriptUrl?: string;
  fallback?: boolean;
  finished?: boolean;
};

/** The subset of Rapyd's client checkout response we rely on. */
type RapydClientCheckout = {
  data?: {
    status?: string;
    payment?: {
      status?: string;
      paid?: boolean;
      next_action?: string;
      redirect_url?: string;
      failure_message?: string;
    };
  };
};

const RAPYD_CLIENT_CHECKOUT = 'https://api.rapyd.net/v1/checkout/client/';

/**
 * The two pages Rapyd hands out for 3DS, in the order they happen:
 *
 *  1. method.html    — the EMV "3DS Method": the issuer fingerprints the device. It has NO UI and
 *                      must simply be loaded so it can post the fingerprint; only once it has run
 *                      does Rapyd swap redirect_url over to the challenge.
 *  2. challenge.html — the actual bank challenge the shopper interacts with.
 *
 * Matching only the challenge is why a real card stalled: its issuer asked for the Method step
 * first, we never rendered it, so the fingerprint was never posted and redirect_url sat on
 * method.html indefinitely. Whatever Rapyd points redirect_url at, we load.
 */
const THREE_DS_PAGE = /3ds-v2\/(method|challenge)\.html/i;
const IS_METHOD_STEP = /3ds-v2\/method\.html/i;

/**
 * The same call Rapyd's own hosted checkout page makes to decide what a payment needs next. Public
 * by design (the checkout id is the capability), so this is a plain credential-less fetch — never
 * send cookies to it.
 */
async function fetchRapydCheckout(checkoutId: string): Promise<RapydClientCheckout | null> {
  try {
    const res = await fetch(
      `${RAPYD_CLIENT_CHECKOUT}${encodeURIComponent(checkoutId)}?is_sdk=1`,
      { credentials: 'omit' },
    );
    if (!res.ok) return null;
    return (await res.json()) as RapydClientCheckout;
  } catch {
    return null;
  }
}

function deviceContext(): Record<string, unknown> {
  const nav = window.navigator || ({} as Navigator);
  const scr = window.screen || ({} as Screen);
  const conn = (nav as unknown as { connection?: { effectiveType?: string } }).connection || {};
  let timezone = '';
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    // Intl unavailable — send blank, the backend treats every context field as optional.
  }
  // Sent so the backend can record where this 3DS page was actually opened from — the only
  // shopper-side evidence that exists when the merchant posts card details straight to the API
  // (see PaymentAcquirerOrchestrator#captureThreeDsPageSignals). ancestorOrigins is filled in by
  // the browser itself for a framed document, so unlike a declared storefront URL it can't be
  // faked by the embedding page. Both are read defensively: ancestorOrigins is unsupported in
  // Firefox, and reading it can throw in a sandboxed frame.
  let ancestorOrigins: string[] = [];
  try {
    const origins = (window.location as unknown as { ancestorOrigins?: DOMStringList }).ancestorOrigins;
    if (origins) {
      ancestorOrigins = Array.from(origins).slice(0, 5);
    }
  } catch {
    // Not available here — the backend treats every context field as optional.
  }
  let documentReferrer = '';
  try {
    documentReferrer = document.referrer || '';
  } catch {
    // Ditto.
  }

  return {
    ancestor_origins: ancestorOrigins,
    document_referrer: documentReferrer,
    timezone,
    timezone_offset_minutes: -new Date().getTimezoneOffset(),
    locale: nav.language || '',
    languages: (nav.languages || []).slice(0, 5),
    screen_width: scr.width || 0,
    screen_height: scr.height || 0,
    pixel_ratio: window.devicePixelRatio || 1,
    viewport_width: window.innerWidth || 0,
    viewport_height: window.innerHeight || 0,
    hw_concurrency: nav.hardwareConcurrency || 0,
    device_memory: (nav as unknown as { deviceMemory?: number }).deviceMemory || 0,
    max_touch_points: nav.maxTouchPoints || 0,
    connection_effective_type: conn.effectiveType || '',
  };
}

function isEmbedded(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

export default function RapydInlineCheckout({
  transactionId,
  paymentUrl,
  resume = false,
}: {
  /** The masked-checkout transaction id — /payments/checkout/{transactionId}/rapyd-session. */
  transactionId: string;
  /** The masked checkout URL from the pay response; used by the explicit fallback button. */
  paymentUrl: string;
  /**
   * "Resume 3DS" mode: the card was already submitted to Rapyd server-side (the direct-card / API
   * flow), so there is nothing to enter here — skip the Checkout Toolkit entirely and go straight to
   * hosting the bank challenge in our iframe, with the same teardown + polling as the inline flow.
   * This is what keeps the acquirer's post-3DS return page out of view: the redirect happens inside
   * the (blocked) iframe, never top-level.
   */
  resume?: boolean;
}) {
  const [ready, setReady] = useState(false);
  /** Rapyd's 3DS challenge page, hosted in our own iframe. */
  const [challengeUrl, setChallengeUrl] = useState('');
  /** Challenge done, waiting on our backend for the authoritative outcome. */
  const [verifying, setVerifying] = useState(false);
  /** Polling gave up without a verdict — tell the shopper instead of spinning forever. */
  const [stalled, setStalled] = useState(false);
  /**
   * Tears the challenge iframe down the moment the bank step ends. Set by the effect; also fired by
   * the iframe's own load event, which is the earliest possible signal (see the render below).
   */
  const endChallengeRef = useRef<((why: string) => void) | null>(null);
  const challengeLoadsRef = useRef(0);
  /** Which 3DS page challengeUrl currently holds — the load-count teardown heuristic below only
   *  applies once this is 'challenge': method.html may reload itself while fingerprinting, and our
   *  own deliberate src-swap from method to challenge must not be mistaken for the ACS navigating
   *  away. */
  const challengeStageRef = useRef<'method' | 'challenge' | ''>('');
  /** The tab opened by the fallback button, so it can be closed once the payment resolves. */
  const fallbackTabRef = useRef<Window | null>(null);
  // Fallback only: URL to finish the payment in a new tab (button shown if the popup was blocked).
  const [continueUrl, setContinueUrl] = useState('');
  /** Fallback surfaced as an explicit button — never an automatic redirect. */
  const [needsContinue, setNeedsContinue] = useState(false);
  const [awaitingTab, setAwaitingTab] = useState(false);
  const [done, setDone] = useState<null | 'success' | 'error'>(null);

  useEffect(() => {
    let cancelled = false;
    let watchdog = 0;
    let pollTimer = 0;
    let settled = false;
    let polling = false;
    let advancing = false;
    const teardown: Array<() => void> = [];
    const embedded = isEmbedded();
    const log = (...a: unknown[]) => console.log('[rapyd]', ...a);

    const outcomeOf = (status: string): 'success' | 'error' =>
      /COMPLETED|SUCCESS|PAID|CAPTURED/i.test(status) ? 'success' : 'error';

    const finish = (status: string, statusUrl?: string) => {
      if (settled || cancelled) return;
      settled = true;
      window.clearTimeout(pollTimer);
      // If the shopper took the fallback step in a tab, close it — that tab is the only place an
      // acquirer page is ever shown, so it should not outlive the payment.
      const tab = fallbackTabRef.current;
      if (tab && !tab.closed) {
        try {
          tab.close();
        } catch {
          // Cross-origin at the moment of close — nothing to do; the shopper can close it.
        }
      }
      const outcome = outcomeOf(status);
      log('finish', status, outcome, embedded ? '(embedded: postMessage)' : '(top: navigate)');
      // Drop the challenge iframe immediately. By this point Rapyd has sent it on to the acquirer's
      // return URL, which their CSP blocks from rendering — so it is a dead frame, and leaving it up
      // is the one way an acquirer error page could ever flash into view.
      setChallengeUrl('');
      if (embedded) {
        try {
          window.parent.postMessage(
            { type: 'moneyeu.checkout.result', outcome, status, transactionId },
            '*',
          );
        } catch {
          // Parent unreachable — the on-page message below still tells the shopper.
        }
        setAwaitingTab(false);
        setDone(outcome);
      } else if (statusUrl) {
        // Navigate to the status page on THIS origin, not the host the backend baked into statusUrl.
        // They match in a real deploy (frontend + backend share a domain), but in local/dev the
        // page is served from the frontend (:4202) while statusUrl carries the public/ngrok host,
        // which points at the backend and has no status PAGE — a cross-origin jump there 401s. Keep
        // the path, drop the host.
        let sameOrigin = statusUrl;
        try {
          const u = new URL(statusUrl, window.location.origin);
          sameOrigin = u.pathname + u.search + u.hash;
        } catch {
          // Malformed — fall back to the raw value.
        }
        window.location.assign(sameOrigin);
      } else {
        setDone(outcome);
      }
    };

    /**
     * The instant the bank step ends, drop the challenge iframe.
     *
     * This matters for more than tidiness: when the ACS finishes, Rapyd's challenge page redirects
     * ITSELF to the acquirer's return URL, which that acquirer's `frame-ancestors 'self'` then
     * refuses to render — so leaving the iframe up even briefly paints the browser's "refused to
     * connect" error inside our checkout. Tearing it down here is what keeps that invisible; the
     * backend poll is still the thing that decides the actual outcome.
     */
    const endChallenge = (why: string) => {
      if (settled || cancelled) return;
      log('challenge ended —', why);
      setChallengeUrl('');
      setVerifying(true);
    };
    endChallengeRef.current = endChallenge;
    teardown.push(() => {
      endChallengeRef.current = null;
    });

    // Every tick hits our status endpoint, which re-asks the acquirer for the real outcome — that is
    // what lets the payment resolve here without the shopper ever landing on the acquirer's own
    // return page. It costs one outbound acquirer call per tick, so stay responsive while the shopper
    // is actually watching (right after the challenge) and then back off, with a hard deadline so a
    // tab left open overnight stops polling the acquirer.
    const poll = (statusUrl?: string) => {
      if (polling) return;
      polling = true;
      const startedAt = Date.now();
      let attempt = 0;
      const tick = async () => {
        if (settled || cancelled) return;
        try {
          const res = await apiService.get<{ data?: { status?: string }; status?: string }>(
            `/payments/redirect/status/${transactionId}`,
            { ...CHECKOUT_API_CONFIG, withCredentials: false },
          );
          const status = (res?.data?.status ?? res?.status ?? '') as string;
          if (status && !/PENDING|PROCESSING|AWAITING/i.test(status)) {
            finish(status, statusUrl);
            return;
          }
        } catch {
          // Transient — keep polling.
        }
        attempt += 1;
        if (Date.now() - startedAt > 10 * 60 * 1000) {
          // Never leave the shopper watching a spinner that will never resolve. The money may well
          // have moved — say so honestly rather than claiming either outcome.
          log('poll deadline reached — giving up');
          setStalled(true);
          return;
        }
        pollTimer = window.setTimeout(tick, attempt < 10 ? 3000 : 6000);
      };
      pollTimer = window.setTimeout(tick, 3000);
    };
    teardown.push(() => window.clearTimeout(pollTimer));

    // Last resort, used only when the challenge URL can't be obtained at all.
    //
    // This deliberately NEVER navigates by itself. A shopper being thrown onto an acquirer-branded
    // page without having touched anything is the exact failure we are engineering against, and an
    // automatic redirect here is the only thing in this component still capable of it (Rapyd's own
    // frame cannot navigate us: nothing in their bundles touches top.location or parent.location).
    // So we surface the step and let the shopper choose to take it.
    const leave = (url: string, statusUrl?: string) => {
      if (cancelled || settled) return;
      log('fallback needed ->', url);
      setContinueUrl(url);
      setNeedsContinue(true);
      setReady(true);
      window.clearTimeout(watchdog);
      // Keep polling underneath: the payment may well resolve on its own, in which case finish()
      // takes over and this step is never taken at all.
      poll(statusUrl);
    };

    (async () => {
      let session: SessionResponse;
      try {
        const res = await apiService.post<{ data?: SessionResponse } | SessionResponse>(
          `/payments/checkout/${transactionId}/rapyd-session`,
          deviceContext(),
          { ...CHECKOUT_API_CONFIG, timeout: 90000 },
        );
        session = (res as { data?: SessionResponse })?.data ?? (res as SessionResponse);
        log('session', JSON.stringify(session).slice(0, 300));
      } catch {
        leave(paymentUrl);
        return;
      }
      if (cancelled) return;

      if (session.finished && session.statusUrl) {
        finish('COMPLETED', session.statusUrl);
        return;
      }
      if (session.fallback || !session.checkoutId || !session.toolkitScriptUrl) {
        log('fallback branch', session.fallback, session.checkoutId, session.toolkitScriptUrl);
        leave(session.directUrl || paymentUrl, session.statusUrl);
        return;
      }
      const checkoutId = session.checkoutId;

      /**
       * Puts a 3DS page in our iframe and records which stage it is — see challengeStageRef's doc
       * for why the stage (not just the URL) has to be tracked. Idempotent: calling this again with
       * the SAME url/stage (the method step polls once a second while waiting) sets React state to
       * an unchanged value, which does not touch the DOM, so the iframe is not reloaded.
       */
      const hostChallengeStage = (url: string, stage: 'method' | 'challenge') => {
        if (challengeStageRef.current !== stage) {
          challengeStageRef.current = stage;
          challengeLoadsRef.current = 0;
        }
        setChallengeUrl(url);
        setReady(true);
      };

      /**
       * While the challenge is up, watch Rapyd's own state directly. This is the same CORS-open
       * endpoint their hosted page polls, so it flips as soon as the ACS result is processed —
       * typically on the very request that also redirects the frame at the acquirer. Reacting here
       * rather than on our backend poll is what closes the window in which the acquirer's blocked
       * page could paint.
       */
      const watchChallenge = async () => {
        for (let i = 0; i < 240 && !settled && !cancelled; i++) {
          await delay(1000);
          if (settled || cancelled) return;
          const payment = (await fetchRapydCheckout(checkoutId))?.data?.payment;
          if (!payment) continue;
          // Starts ACT / paid:false / next_action:3d_verification — any move off that means the
          // bank step is over, whichever way it went.
          const leftChallenge =
            payment.paid === true ||
            /^(CLO|ERR|REJ|FAI)/i.test(payment.status || '') ||
            (!!payment.next_action && payment.next_action !== '3d_verification');
          if (leftChallenge) {
            endChallenge(`rapyd status=${payment.status} paid=${payment.paid}`);
            return;
          }
        }
      };

      /**
       * Rapyd fires "success" the instant the payment is CREATED — before 3DS. That is our cue to
       * find out what the payment actually needs next, the same way Rapyd's own hosted page does.
       */
      const advance = async (label: string) => {
        if (settled || cancelled) return;
        // The toolkit can announce the same moment more than once (onCheckoutPaymentSuccess plus
        // onCheckoutPaymentPending plus the '3d-secure-redirect' message all fire for one payment).
        // Without this guard each would run its own retry loop and its own challenge watcher, and —
        // worst case — one could time out into the popup fallback while another had already put the
        // challenge on screen, leaving the shopper with both.
        if (advancing) return;
        advancing = true;
        log(label);
        window.clearTimeout(watchdog);
        poll(session.statusUrl);

        // Wait generously for the challenge URL. A real card runs the EMV "3DS Method" step —
        // the issuer fingerprinting the device — BEFORE a challenge URL exists, and the spec allows
        // ~10s for that alone; test cards skip it entirely. An 8s budget here was the reason a real
        // card fell through to the hosted-page fallback (and so revealed the acquirer) while every
        // test card stayed inline. Falling back early is far worse than waiting: the shopper is
        // looking at Rapyd's own pending spinner the whole time.
        for (let i = 0; i < 75 && !settled && !cancelled; i++) {
          const info = await fetchRapydCheckout(checkoutId);
          const payment = info?.data?.payment;
          const url = payment?.redirect_url || '';
          // Terminal states first: after the method step an issuer can approve or decline
          // frictionlessly while redirect_url still points at method.html, and checking the URL
          // first would keep "waiting for the challenge" on a payment that is already over.
          if (payment?.paid === true) {
            log('payment already paid, no challenge needed');
            finish('COMPLETED', session.statusUrl);
            return;
          }
          if (payment?.status && /^(CLO|ERR|REJ)/i.test(payment.status) && payment.paid === false) {
            log('payment closed without challenge:', payment.status, payment.failure_message);
            finish('FAILED', session.statusUrl);
            return;
          }
          if (IS_METHOD_STEP.test(url)) {
            // No UI of its own — just has to load so it can post the device fingerprint to the
            // issuer. Keep polling; Rapyd swaps redirect_url to challenge.html once that's done.
            log('3DS method URL resolved — loading it inline to post the device fingerprint:', url);
            hostChallengeStage(url, 'method');
            await delay(1000);
            continue;
          }
          if (THREE_DS_PAGE.test(url)) {
            log('3DS challenge URL resolved — hosting it inline:', url);
            hostChallengeStage(url, 'challenge');
            void watchChallenge();
            return;
          }
          await delay(1000);
        }

        if (settled || cancelled) return;
        // Couldn't get a challenge URL — rather than strand the shopper, finish on Rapyd's own page.
        log('no challenge URL after retries — falling back to hosted page');
        leave(session.hostedPageUrl || session.directUrl || paymentUrl, session.statusUrl);
      };

      // Resume-3DS: the card is already in at Rapyd, so don't mount the toolkit — jump straight to
      // finding and hosting the challenge. advance() polls Rapyd's own state, hosts method.html then
      // challenge.html in our iframe, tears it down when the bank step ends, and polls our backend
      // for the outcome — Collect Group's return page never renders because it's caught in the frame.
      if (resume) {
        setReady(true);
        void advance('resume-3ds');
        return;
      }

      const onLoading = () => {
        log('onLoading (frame booted)');
        window.clearTimeout(watchdog);
        setReady(true);
      };
      const onPendingEvt = () => void advance('onCheckoutPaymentPending / 3ds');
      const onSuccess = () => void advance('onCheckoutPaymentSuccess (create)');
      const onFailure = () => {
        log('onCheckoutPaymentFailure');
        window.clearTimeout(watchdog);
        finish('FAILED', session.statusUrl);
      };
      const on3ds = (event: MessageEvent) => {
        if (event.data === '3d-secure-redirect') onPendingEvt();
      };
      window.addEventListener('message', on3ds);
      window.addEventListener('onLoading', onLoading);
      window.addEventListener('onCheckoutPaymentPending', onPendingEvt);
      window.addEventListener('onCheckoutPaymentSuccess', onSuccess);
      window.addEventListener('onCheckoutPaymentFailure', onFailure);
      window.addEventListener('onCheckoutFailure', onFailure);
      teardown.push(() => {
        window.removeEventListener('message', on3ds);
        window.removeEventListener('onLoading', onLoading);
        window.removeEventListener('onCheckoutPaymentPending', onPendingEvt);
        window.removeEventListener('onCheckoutPaymentSuccess', onSuccess);
        window.removeEventListener('onCheckoutPaymentFailure', onFailure);
        window.removeEventListener('onCheckoutFailure', onFailure);
      });

      const script = document.createElement('script');
      script.src = session.toolkitScriptUrl;
      script.onload = () => {
        try {
          const Toolkit = (window as unknown as { RapydCheckoutToolkit?: new (o: object) => { displayCheckout: () => void } })
            .RapydCheckoutToolkit;
          if (!Toolkit) {
            leave(session.hostedPageUrl || session.directUrl || paymentUrl, session.statusUrl);
            return;
          }
          log('toolkit loaded, displayCheckout()');
          new Toolkit({
            id: checkoutId,
            pay_button_text: 'Pay now',
            pay_button_color: '#4f6bff',
            // MUST stay false. The toolkit forwards this into Rapyd's own checkout frame, and when
            // it is true that frame navigates to the checkout's complete_checkout_url on its own —
            // straight to Rapyd's hosted page and then the acquirer's, with no way for us to
            // intervene. It was true back when we deliberately handed 3DS to the hosted page; now
            // that the challenge is hosted here, it just leaks the acquirer. Events still fire.
            redirect: false,
            close_on_complete: false,
          }).displayCheckout();
        } catch {
          leave(session.hostedPageUrl || session.directUrl || paymentUrl, session.statusUrl);
        }
      };
      script.onerror = () => {
        log('toolkit script failed to load');
        leave(session.hostedPageUrl || session.directUrl || paymentUrl, session.statusUrl);
      };
      document.body.appendChild(script);
      teardown.push(() => {
        script.remove();
        document.getElementById('rapyd-checkout-frame')?.remove();
      });

      // Last resort: the frame never signals it booted (third-party cookies blocked, or the toolkit
      // assets couldn't load). Finish on Rapyd's page rather than leaving the shopper on a dead frame.
      watchdog = window.setTimeout(() => {
        log('WATCHDOG fired (no onLoading)');
        leave(session.hostedPageUrl || session.directUrl || paymentUrl, session.statusUrl);
      }, 45000);
    })();

    return () => {
      cancelled = true;
      window.clearTimeout(watchdog);
      window.clearTimeout(pollTimer);
      teardown.forEach((fn) => fn());
    };
  }, [transactionId, paymentUrl, resume]);

  if (done) {
    return (
      <div className="checkout-rapyd-inline" style={{ textAlign: 'center', padding: '20px 8px' }}>
        {done === 'success' ? (
          <p className="checkout-status-sub">Payment complete — thank you.</p>
        ) : (
          <p className="checkout-field-err">Your payment was not completed. Please try again.</p>
        )}
      </div>
    );
  }

  // The bank's 3DS challenge, hosted in our own card. Rapyd's challenge page sets no framing
  // headers, so this renders where the toolkit refused to.
  if (challengeUrl) {
    return (
      <div className="checkout-rapyd-inline">
        <iframe
          src={challengeUrl}
          title="Bank verification"
          onLoad={() => {
            // The method step may reload itself while it fingerprints, and our own deliberate
            // src-swap from method.html to challenge.html fires a load too — neither means the ACS
            // navigated away, so only count loads once we're actually on the challenge page. Load
            // #1 there is the challenge page itself; any load after that is Rapyd sending this frame
            // on to the acquirer's return URL, which their CSP will refuse to render — our earliest
            // cue to take the frame down before that error paints.
            if (challengeStageRef.current !== 'challenge') return;
            challengeLoadsRef.current += 1;
            if (challengeLoadsRef.current > 1) {
              endChallengeRef.current?.('iframe navigated past the challenge');
            }
          }}
          // Fixed to the standard EMV 3DS challenge window (500x600) rather than a viewport-relative
          // height: the ACS draws at one of a few fixed sizes, so sizing off the viewport just left
          // the frame over- or under-sized depending on the screen. Falls back to full width on a
          // phone.
          style={{
            display: 'block',
            width: '100%',
            maxWidth: 500,
            height: 600,
            margin: '0 auto',
            border: 0,
            background: 'transparent',
          }}
        />
      </div>
    );
  }

  // Bank step done, backend confirming the outcome. Nothing acquirer-branded is on screen here.
  if (verifying || stalled) {
    return (
      <div className="checkout-rapyd-inline" style={{ textAlign: 'center', padding: '28px 8px' }}>
        {stalled ? (
          <p className="checkout-status-sub">
            Your payment is still being confirmed. Please don't pay again — you'll receive
            confirmation by email once it settles.
          </p>
        ) : (
          <p className="checkout-status-sub">Confirming your payment&hellip;</p>
        )}
      </div>
    );
  }

  // Fallback: we could not host the bank step ourselves. Offer it; never take it for the shopper.
  if (needsContinue && !awaitingTab) {
    return (
      <div className="checkout-rapyd-inline" style={{ textAlign: 'center', padding: '26px 12px' }}>
        <p className="checkout-status-sub">
          Your bank needs one more step to confirm this payment.
        </p>
        <button
          type="button"
          onClick={() => {
            if (isEmbedded()) {
              fallbackTabRef.current = window.open(continueUrl, '_blank');
              setAwaitingTab(true);
            } else {
              window.location.assign(continueUrl);
            }
          }}
          style={{
            marginTop: 16,
            minWidth: 220,
            height: 44,
            border: 0,
            borderRadius: 10,
            background: '#4f6bff',
            color: '#fff',
            font: '600 14px inherit',
            cursor: 'pointer',
          }}
        >
          Continue securely
        </button>
      </div>
    );
  }

  // The shopper took the fallback step in a new tab; wait for the result here.
  if (awaitingTab) {
    return (
      <div className="checkout-rapyd-inline" style={{ textAlign: 'center', padding: '20px 8px' }}>
        <p className="checkout-status-sub">
          Complete your payment in the new window. This page updates automatically when it's done.
        </p>
        {continueUrl && (
          <p className="checkout-hint" style={{ marginTop: 12 }}>
            <a href={continueUrl} target="_blank" rel="noopener noreferrer">
              Window didn't open? Continue to payment
            </a>
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="checkout-rapyd-inline">
      {!ready && (
        <div className="checkout-status-sub" style={{ textAlign: 'center', padding: '24px 0' }}>
          Loading secure payment&hellip;
        </div>
      )}
      <div id="rapyd-toolkit" />
    </div>
  );
}
